import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTrigger,
  EmptyState,
  Field,
  FieldLabel,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  useToast,
} from "@forge/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, CreditCard, Plus, UserRoundCog } from "lucide-react";
import { useState } from "react";
import { AddUserDialog } from "../../components/add-user-dialog";
import { PlatformHealthCard } from "../../components/platform-health";
import { PlatformMetricsCard } from "../../components/platform-metrics";
import { TenantBillingPanel } from "../../components/tenant-billing";
import { api, unwrap } from "../../lib/api";
import {
  ACTION_LABELS,
  type AdminTenantSummary,
  formatDate,
  formatDateTime,
  pressLinkClassName,
  SUBSCRIPTION_LABELS,
  TENANT_STATUS,
  usageLabel,
} from "../../lib/tenants";

export const Route = createFileRoute("/_admin/")({
  staticData: { layout: "wide" },
  component: TenantsPage,
});

function TenantsPage() {
  const tenants = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: async () => unwrap(await api.admin.tenants.get()),
  });

  return (
    <>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-base font-semibold tracking-tight text-ink">
            Organizações
          </h1>
          <p className="mt-0.5 text-2xs text-ink-muted">
            {tenants.data?.tenants.length ?? 0} clientes na plataforma
          </p>
        </div>
        <CreateTenantDialog />
      </div>

      <div className="flex flex-col gap-3">
      <div className="grid items-start gap-3 lg:grid-cols-2">
        <PlatformHealthCard />
        <PlatformMetricsCard />
      </div>
      <Card className="animate-rise [animation-delay:80ms]">
        {tenants.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="size-5 text-ink-muted" />
          </div>
        ) : tenants.data && tenants.data.tenants.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Organização</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Plano</TableHead>
                <TableHead>Uso</TableHead>
                <TableHead className="text-right">Usuários</TableHead>
                <TableHead className="w-28" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.data.tenants.map((tenant) => (
                <TenantRow key={tenant.id} tenant={tenant} />
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            icon={<Building2 />}
            title="Nenhuma organização"
            description="Crie a primeira organização para começar."
            action={<CreateTenantDialog />}
          />
        )}
      </Card>
      <AuditLogCard />
      </div>
    </>
  );
}

function AuditLogCard() {
  const audit = useQuery({
    queryKey: ["admin", "audit"],
    queryFn: async () => unwrap(await api.admin.audit.get({ query: { limit: 40 } })),
  });

  return (
    <Card>
      <div className="border-b border-line px-4 py-3">
        <p className="text-[0.8125rem] font-semibold text-ink">Auditoria</p>
        <p className="mt-0.5 text-2xs text-ink-muted">
          Quem fez o quê no painel — cobrança, usuários e sessão de suporte.
        </p>
      </div>
      {audit.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : audit.data && audit.data.entries.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Quando</TableHead>
              <TableHead>Quem</TableHead>
              <TableHead>Ação</TableHead>
              <TableHead>Organização</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {audit.data.entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell className="text-2xs text-ink-muted">
                  {formatDateTime(entry.createdAt)}
                </TableCell>
                <TableCell>
                  <p className="text-ink">{entry.actorName ?? "—"}</p>
                  <p className="font-mono text-2xs text-ink-muted">
                    {entry.actorEmail ?? ""}
                  </p>
                </TableCell>
                <TableCell>
                  {ACTION_LABELS[entry.action] ?? entry.action}
                  {entry.action === "create_user" &&
                  entry.context &&
                  typeof entry.context.email === "string"
                    ? ` · ${entry.context.email}`
                    : ""}
                </TableCell>
                <TableCell className="text-ink-muted">
                  {entry.tenantName ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          icon={<Building2 />}
          title="Nenhuma ação registrada"
          description="Estender trial, suspender e entrar como suporte passam a aparecer aqui."
        />
      )}
    </Card>
  );
}

function TenantRow({ tenant }: { tenant: AdminTenantSummary }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const status = TENANT_STATUS[tenant.status] ?? TENANT_STATUS.active!;

  const impersonate = useMutation({
    mutationFn: async () =>
      unwrap(await api.admin.tenants({ id: tenant.id }).impersonate.post()),
    onSuccess: ({ webUrl }) => {
      window.open(webUrl, "_blank");
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      toast.add({
        type: "info",
        title: `Sessão de suporte iniciada em ${tenant.name}`,
        description: "A ação fica registrada na trilha de auditoria.",
      });
    },
  });

  return (
    <TableRow>
      <TableCell>
        <Link
          to="/tenants/$id"
          params={{ id: tenant.id }}
          className={pressLinkClassName}
        >
          {tenant.name}
        </Link>
        <p className="font-mono text-2xs text-ink-muted">{tenant.slug}</p>
      </TableCell>
      <TableCell>
        <Badge variant={status.variant} dot>
          {status.label}
        </Badge>
      </TableCell>
      <TableCell>
        <p className="text-ink">
          {tenant.plan ?? "—"}
          {tenant.subscriptionStatus && (
            <span className="ml-1.5 text-2xs text-ink-muted">
              ({SUBSCRIPTION_LABELS[tenant.subscriptionStatus] ?? tenant.subscriptionStatus})
            </span>
          )}
        </p>
        {tenant.currentPeriodEnd && (
          <p className="text-2xs text-ink-muted">
            até {formatDate(tenant.currentPeriodEnd)}
          </p>
        )}
        {tenant.syncBlocked && (
          <Badge variant="warning" className="mt-1">
            Sync pausado
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <p className="font-mono text-2xs text-ink">
          {usageLabel(tenant.usage.accounts, tenant.limits.accounts)} lojas
        </p>
        <p className="font-mono text-2xs text-ink-muted">
          {usageLabel(tenant.usage.ordersThisMonth, tenant.limits.ordersPerMonth)}{" "}
          pedidos
        </p>
      </TableCell>
      <TableCell className="text-right font-mono">
        {tenant.memberCount}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-1">
          <BillingDialog tenant={tenant} />
          <AddUserDialog tenantId={tenant.id} tenantName={tenant.name} />
          <Tooltip content="Entrar como suporte">
            <Button
              variant="ghost"
              size="icon"
              loading={impersonate.isPending}
              onClick={() => impersonate.mutate()}
            >
              <UserRoundCog />
            </Button>
          </Tooltip>
        </div>
      </TableCell>
    </TableRow>
  );
}

function CreateTenantDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const queryClient = useQueryClient();
  const toast = useToast();

  const create = useMutation({
    mutationFn: async () =>
      unwrap(await api.admin.tenants.post({ name, slug })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      toast.add({ type: "success", title: `Organização "${name}" criada` });
      setOpen(false);
      setName("");
      setSlug("");
    },
    onError: (error) => {
      toast.add({
        type: "error",
        title: "Não foi possível criar",
        description: error.message,
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button>
            <Plus />
            Nova organização
          </Button>
        }
      />
      <DialogContent
        title="Nova organização"
        description="Cria o tenant e a assinatura inicial (trial)."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <Field>
            <FieldLabel>Nome</FieldLabel>
            <Input
              required
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setSlug(
                  e.target.value
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, ""),
                );
              }}
              placeholder="Empresa Exemplo LTDA"
            />
          </Field>
          <Field>
            <FieldLabel>Identificador</FieldLabel>
            <Input
              required
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="empresa-exemplo"
              className="font-mono"
            />
          </Field>
          <Button type="submit" loading={create.isPending}>
            Criar organização
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BillingDialog({ tenant }: { tenant: AdminTenantSummary }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={
          <Button variant="ghost" size="icon" title="Cobrança">
            <CreditCard />
          </Button>
        }
      />
      <DialogContent
        className="max-w-lg"
        title={`Cobrança — ${tenant.name}`}
        description="Indicadores operacionais. A fatura em si fica na Asaas."
      >
        <TenantBillingPanel tenant={tenant} />
      </DialogContent>
    </Dialog>
  );
}
